import type { AppContext } from '@application/context/AppContext';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { getBusinessApiClient, type RemoteCoffrefortDocument } from '@infra/api/BusinessApiClient';
import { CoffreFortFileBridge } from '@infra/persistence/CoffreFortFileBridge';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { logger } from '@infra/telemetry/Logger';
import { normalizeRegisterList, registerPayload } from '@application/persistence/moduleDataPayload';

export type CoffreFortSyncStatus = 'local' | 'remote' | 'pending_upload';

export interface CoffreFortDocument {
  id: string;
  name: string;
  size: number;
  scope: 'entreprise' | 'salarie';
  salarie_id: string | null;
  category: string;
  uploadedAt: string;
  syncStatus: CoffreFortSyncStatus;
  localPath?: string;
  remoteId?: string;
  mimeType?: string;
  /** Legacy — migré vers localPath au premier chargement. */
  dataUrl?: string;
}

const NAMESPACE = 'coffrefort';
const MAX_BYTES = 10 * 1024 * 1024;

function legacyKey(tenantId: string): string {
  return `nexxus.coffrefort.${tenantId}`;
}

function remoteToLocal(remote: RemoteCoffrefortDocument): CoffreFortDocument {
  return {
    id: remote.id,
    name: remote.name,
    size: remote.size,
    scope: remote.scope as CoffreFortDocument['scope'],
    salarie_id: remote.salarie_id,
    category: remote.category,
    uploadedAt: remote.uploaded_at,
    syncStatus: 'remote',
    remoteId: remote.id,
    mimeType: remote.mime_type ?? undefined,
  };
}

async function loadIndex(tenantId: string): Promise<CoffreFortDocument[]> {
  const data = await SecureTenantStore.getJson<unknown>(
    tenantId,
    NAMESPACE,
    registerPayload([]),
    legacyKey(tenantId),
  );
  return normalizeRegisterList<CoffreFortDocument>(data);
}

/** Index persisté : jamais de dataUrl (les binaires restent sur disque). */
export function indexCoffreSansBinaire(docs: readonly CoffreFortDocument[]): CoffreFortDocument[] {
  return docs.map(({ dataUrl: _dropped, ...rest }) => rest);
}

async function saveIndex(tenantId: string, docs: CoffreFortDocument[]): Promise<void> {
  const sanitized = indexCoffreSansBinaire(docs);
  await SecureTenantStore.setJson(
    tenantId,
    NAMESPACE,
    registerPayload(sanitized),
    legacyKey(tenantId),
  );
}

async function migrateLegacyDoc(
  tenantId: string,
  doc: CoffreFortDocument,
): Promise<CoffreFortDocument> {
  if (!doc.dataUrl?.startsWith('data:') || doc.localPath) return doc;
  if (!CoffreFortFileBridge.isAvailable()) return doc;
  const comma = doc.dataUrl.indexOf(',');
  if (comma < 0) return doc;
  const meta = doc.dataUrl.slice(0, comma);
  const base64 = doc.dataUrl.slice(comma + 1);
  const mimeMatch = /^data:([^;]+)/.exec(meta);
  const mimeType = mimeMatch?.[1] ?? 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const saved = await CoffreFortFileBridge.write(tenantId, doc.id, doc.name, bytes.buffer);
  return {
    ...doc,
    dataUrl: undefined,
    localPath: saved.relativePath,
    mimeType,
    size: saved.size,
    syncStatus: doc.syncStatus ?? 'local',
  };
}

async function ensureMigrated(
  tenantId: string,
  docs: CoffreFortDocument[],
): Promise<CoffreFortDocument[]> {
  let changed = false;
  const out: CoffreFortDocument[] = [];
  for (const doc of docs) {
    const hadLegacy = Boolean(doc.dataUrl);
    const next = await migrateLegacyDoc(tenantId, doc);
    if (hadLegacy && !next.dataUrl) changed = true;
    out.push(next);
  }
  if (changed) await saveIndex(tenantId, out);
  return out;
}

/** Stockage coffre-fort : fichiers locaux + sync S3 via business-api en mode distant. */
export class CoffreFortStorageService {
  static async listDocuments(ctx: AppContext, tenantId: string): Promise<CoffreFortDocument[]> {
    let local = await ensureMigrated(tenantId, await loadIndex(tenantId));

    if (!isRemoteMultiStationMode(ctx.getLockedDataMode?.() ?? null)) {
      return local;
    }

    const api = getBusinessApiClient();
    const online = (await api.confirmWriteReady()) && Boolean(api.hasUserSession());

    if (online) {
      try {
        const remoteItems = await api.listCoffrefortDocuments();
        const pending = local.filter((d) => d.syncStatus === 'pending_upload');
        const merged = [...remoteItems.map(remoteToLocal), ...pending];
        const byId = new Map<string, CoffreFortDocument>();
        for (const doc of merged) byId.set(doc.remoteId ?? doc.id, doc);
        local = [...byId.values()];
        await saveIndex(tenantId, local);
        return local;
      } catch (err) {
        logger.warn({ event: 'coffrefort_remote_list_fallback', error: String(err) });
      }
    }

    return local;
  }

  static async addDocument(
    ctx: AppContext,
    tenantId: string,
    file: File,
    meta: Pick<CoffreFortDocument, 'scope' | 'salarie_id' | 'category'>,
  ): Promise<CoffreFortDocument> {
    if (file.size > MAX_BYTES) {
      throw new Error(`Fichier trop volumineux (max ${MAX_BYTES / (1024 * 1024)} Mo)`);
    }
    if (!CoffreFortFileBridge.isAvailable()) {
      throw new Error('Stockage coffre-fort disponible uniquement dans l’application desktop');
    }

    const id = `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const buffer = await file.arrayBuffer();
    const saved = await CoffreFortFileBridge.write(tenantId, id, file.name, buffer);

    const remoteMode = isRemoteMultiStationMode(ctx.getLockedDataMode?.() ?? null);
    let doc: CoffreFortDocument = {
      id,
      name: file.name,
      size: saved.size,
      scope: meta.scope,
      salarie_id: meta.salarie_id,
      category: meta.category,
      uploadedAt: new Date().toISOString(),
      syncStatus: remoteMode ? 'pending_upload' : 'local',
      localPath: saved.relativePath,
      mimeType: file.type || 'application/octet-stream',
    };

    if (remoteMode) {
      const uploaded = await CoffreFortStorageService.tryUploadRemote(doc, file);
      if (uploaded) doc = uploaded;
    }

    const docs = await loadIndex(tenantId);
    docs.unshift(doc);
    await saveIndex(tenantId, docs);
    return doc;
  }

  static async deleteDocument(ctx: AppContext, tenantId: string, docId: string): Promise<void> {
    const docs = await loadIndex(tenantId);
    const doc = docs.find((d) => d.id === docId);
    if (!doc) return;

    if (doc.localPath && CoffreFortFileBridge.isAvailable()) {
      try {
        await CoffreFortFileBridge.delete(tenantId, doc.localPath);
      } catch {
        /* ignore */
      }
    }

    if (
      isRemoteMultiStationMode(ctx.getLockedDataMode?.() ?? null) &&
      doc.remoteId &&
      (await getBusinessApiClient().confirmWriteReady()) &&
      getBusinessApiClient().hasUserSession()
    ) {
      try {
        await getBusinessApiClient().deleteCoffrefortDocument(doc.remoteId);
      } catch (err) {
        logger.warn({ event: 'coffrefort_remote_delete_failed', docId, error: String(err) });
      }
    }

    await saveIndex(
      tenantId,
      docs.filter((d) => d.id !== docId),
    );
  }

  static async getDownloadBlob(doc: CoffreFortDocument, tenantId: string): Promise<Blob> {
    if (doc.localPath && CoffreFortFileBridge.isAvailable()) {
      try {
        const buffer = await CoffreFortFileBridge.read(tenantId, doc.localPath);
        return new Blob([buffer], { type: doc.mimeType ?? 'application/octet-stream' });
      } catch {
        /* fallback remote */
      }
    }

    if (doc.dataUrl?.startsWith('data:')) {
      const res = await fetch(doc.dataUrl);
      return res.blob();
    }

    const remoteId = doc.remoteId ?? doc.id;
    const api = getBusinessApiClient();
    if ((await api.confirmWriteReady()) && api.hasUserSession()) {
      return api.downloadCoffrefortDocument(remoteId);
    }

    throw new Error('Document indisponible hors connexion');
  }

  static async syncPendingUploads(ctx: AppContext, tenantId: string): Promise<number> {
    if (!isRemoteMultiStationMode(ctx.getLockedDataMode?.() ?? null)) return 0;
    const api = getBusinessApiClient();
    if (!(await api.confirmWriteReady()) || !api.hasUserSession()) return 0;

    const docs = await loadIndex(tenantId);
    let synced = 0;
    const next: CoffreFortDocument[] = [];

    for (const doc of docs) {
      if (doc.syncStatus !== 'pending_upload' || !doc.localPath) {
        next.push(doc);
        continue;
      }
      try {
        const buffer = await CoffreFortFileBridge.read(tenantId, doc.localPath);
        const file = new File([buffer], doc.name, {
          type: doc.mimeType ?? 'application/octet-stream',
        });
        const uploaded = await CoffreFortStorageService.tryUploadRemote(doc, file);
        if (uploaded) {
          synced += 1;
          next.push(uploaded);
        } else {
          next.push(doc);
        }
      } catch (err) {
        logger.warn({ event: 'coffrefort_sync_pending_failed', docId: doc.id, error: String(err) });
        next.push(doc);
      }
    }

    if (synced > 0) await saveIndex(tenantId, next);
    return synced;
  }

  private static async tryUploadRemote(
    doc: CoffreFortDocument,
    file: File,
  ): Promise<CoffreFortDocument | null> {
    const api = getBusinessApiClient();
    if (!(await api.confirmWriteReady()) || !api.hasUserSession()) return null;
    try {
      const remote = await api.uploadCoffrefortDocument(file, {
        scope: doc.scope,
        salarie_id: doc.salarie_id,
        category: doc.category,
      });
      return {
        ...doc,
        syncStatus: 'remote',
        remoteId: remote.id,
        id: remote.id,
        uploadedAt: remote.uploaded_at,
        size: remote.size,
      };
    } catch (err) {
      logger.warn({
        event: 'coffrefort_remote_upload_deferred',
        docId: doc.id,
        error: String(err),
      });
      return null;
    }
  }
}
